import { create } from 'zustand';
import { atributosRowsToDbPayload, normalizeAtributosFromDb } from '../utils/productoAtributos';
import { Producto } from '../types';
import { productoService } from '../services/supabaseService';
import { supabase } from '../lib/supabase';
import { fetchByEmpresaId } from '../utils/supabaseHelpers';
import { handleStoreAction } from '../utils/storeHelpers';
import { logger } from '../utils/logger';
// Per-product low-stock alerts are disabled: inventory alerts are now a digest
// (Productos agotados / Stock bajo) from check_low_inventory() via the nightly
// cron (netlify/functions/cron-check-alerts). See migration 20260619120000.
// import { createLowInventoryAlert } from '../lib/alerts/alertGenerator';
// getCurrentEmpresaIdSync was only used by the disabled per-product low-stock alert.
// import { getCurrentEmpresaIdSync } from '../utils/empresaUtils';
import { resolveProductoEstadoFromDb } from '../utils/productoEstado';

/** Coalesce concurrent fetchProductos(empresaId) (p. ej. Productos + Membresías). */
const fetchProductosInFlight = new Map<string, Promise<Producto[] | null>>();

/** Keep only valid image URLs (no empty or "Empty Title" placeholders). Export for use in ModalProducto/ImageGallery. */
export function filterValidImageUrls(imagenes: unknown[]): string[] {
  if (!Array.isArray(imagenes)) return [];
  return imagenes
    .map((item): string | null => {
      if (typeof item === 'string') {
        const s = (item || '').trim();
        return (s.length > 0 && (s.startsWith('http') || s.startsWith('data:'))) ? s : null;
      }
      if (item && typeof item === 'object' && 'url' in item) {
        const u = (item as { url?: string }).url;
        return typeof u === 'string' && u.trim().length > 0 ? u.trim() : null;
      }
      return null;
    })
    .filter((u): u is string => u != null);
}

interface ProductosState {
  productos: Producto[];
  productoSeleccionado: Producto | null;
  isLoading: boolean;
  error: string | null;
  filtros: {
    categoria: string;
    estado: string;
  };
  paginacion: {
    pagina: number;
    porPagina: number;
    total: number;
  };
  
  // Actions
  fetchProductos: (empresaId: string) => Promise<Producto[] | null>;
  fetchProducto: (id: string) => Promise<void>;
  crearProducto: (producto: Partial<Producto>, empresaId: string) => Promise<Producto | null>;
  actualizarProducto: (id: string, producto: Partial<Producto>, empresaId: string) => Promise<Producto | null>;
  /** Solo stock + mínimo (lista productos); no dispara isLoading global. */
  actualizarStockEnLinea: (id: string, stock: number, stockMinimo: number, empresaId: string) => Promise<boolean>;
  eliminarProducto: (id: string) => Promise<boolean>;
  archivarProducto: (id: string) => Promise<boolean>;
  restaurarProducto: (id: string) => Promise<boolean>;
  ajustarStock: (id: string, cantidad: number, tipo: 'ENTRADA' | 'SALIDA') => Promise<void>;
  ajustarStockMultiple: (items: Array<{ productoId: string; cantidad: number }>, tipo: 'ENTRADA' | 'SALIDA') => Promise<Array<{ productoId: string; success: boolean; error?: string; previousStock?: number; newStock?: number }>>;
  setProductoSeleccionado: (producto: Producto | null) => void;
  setFiltros: (filtros: any) => void;
  setPaginacion: (paginacion: any) => void;
  clearError: () => void;
  clearState: () => void;
  // Multiple images
  uploadProductImages: (productoId: string, files: File[]) => Promise<string[]>;
  removeProductImage: (productoId: string, imageIndex: number) => Promise<void>;
  reorderProductImages: (productoId: string, fromIndex: number, toIndex: number) => Promise<void>;
  setPrimaryImage: (productoId: string, imageIndex: number) => Promise<void>;
  findByBarcode: (barcode: string) => Producto | undefined;
  generateSKU: (empresaId: string, categoria?: string) => Promise<string>;
}

// Helper function to convert Supabase product to local format (exported for factura→venta resolution)
export const convertSupabaseToLocal = (p: any): Producto => {
  // DEBUG: Log IVA conversion for "Under Amour" products
  if (p.nombre?.includes('Under Amour')) {
    console.log('🔍 convertSupabaseToLocal (Under Amour):', {
      nombre: p.nombre,
      iva_porcentaje_from_db: p.iva_porcentaje,
      iva_porcentaje_final: p.iva_porcentaje !== null && p.iva_porcentaje !== undefined ? p.iva_porcentaje : 0
    });
  }
  
  const tipoOfertaDb: Producto['tipoOferta'] =
    (p.tipo_oferta as Producto['tipoOferta']) ||
    (p.es_suscripcion ? 'membresia' : p.inventory_type === 'unlimited' ? 'servicio' : 'producto');
  const sinStockFisico =
    tipoOfertaDb === 'servicio' || tipoOfertaDb === 'membresia' || tipoOfertaDb === 'listado';
  const inventoryTypeFinal: 'tracked' | 'unlimited' =
    sinStockFisico || p.inventory_type === 'unlimited' ? 'unlimited' : 'tracked';

  return {
    id: p.id,
    nombre: p.nombre,
    descripcion: p.descripcion || '',
    sku: p.sku || p.codigo || '',
    barcode: p.barcode || undefined,
    categoria: p.categoria || '',
    precioCompra: p.precio_compra || 0,
    precioVenta: p.precio || 0,
    precioSinIva: p.precio || 0,
    valorIva: 0,
    ivaPorcentaje: p.iva_porcentaje !== null && p.iva_porcentaje !== undefined ? p.iva_porcentaje : 0,
    stock: p.stock ?? 0,
    stockMinimo:
      p.stock_minimo !== undefined && p.stock_minimo !== null
        ? p.stock_minimo
        : inventoryTypeFinal === 'unlimited'
          ? 0
          : 5,
    unidadMedida: p.unidad_medida || 'Unidad',
    inventory_type: inventoryTypeFinal,
    estado: resolveProductoEstadoFromDb(p, inventoryTypeFinal),
    fechaArchivado: p.fecha_archivado ? new Date(p.fecha_archivado) : null,
    imagen: (() => {
      const list = filterValidImageUrls(Array.isArray(p.imagenes) ? p.imagenes : (p.imagen ? [p.imagen] : []));
      return list.length > 0 ? list[0] : (p.imagen && (p.imagen as string).trim() ? p.imagen : '');
    })(),
    imagenes: filterValidImageUrls(Array.isArray(p.imagenes) ? p.imagenes : (p.imagen ? [p.imagen] : [])),
    mostrarEnCatalogo: p.mostrar_en_catalogo !== false,
    destacado: p.destacado || false,
    disponibleOnline: p.disponible_online !== false,
    createdAt: new Date(p.created_at || new Date()),
    updatedAt: new Date(p.updated_at || new Date()),
    empresaId: p.empresa_id || '',
    productoGlobalId: p.producto_global_id || undefined,
    // Additional fields with defaults
    margenBruto: p.precio && p.precio_compra 
      ? (p.precio - p.precio_compra) / p.precio 
      : 0,
    proveedor: p.proveedor || '',
    fechaUltimaCompra: undefined,
    ventasUltimos30Dias: 0,
    rentabilidadUltimos30Dias: 0,
    rotacionInventario: 0,
    mostrarPrecio: p.mostrar_precio !== false,
    mostrarStock: p.mostrar_stock !== false,
    ordenCatalogo: p.orden_catalogo || 0,
    tags: p.tags || [],
    tiempoPreparacion: p.tiempo_preparacion || '',
    ubicacion: p.ubicacion || '',
    esSuscripcion: p.es_suscripcion || false,
    frecuenciaCobro: p.frecuencia_cobro || undefined,
    clasesIncluidas: p.clases_incluidas ?? undefined,
    duracionDias: p.duracion_dias || undefined,
    autoRenovar: p.auto_renovar !== false,
    generaQrCode: p.genera_qr_code !== false,
    requiereCheckin: p.requiere_checkin !== false,
    tipoOferta: tipoOfertaDb,
    tieneAgenda: p.tiene_agenda === true,
    accesoLibre: p.acceso_libre === true,
    incluyeGrupal: p.incluye_grupal === true,
    incluyePrivada: p.incluye_privada === true,
    tipoListado: (p.tipo_listado as Producto['tipoListado']) ?? null,
    ubicacionListado: p.ubicacion_listado ?? undefined,
    areaM2: p.area_m2 != null ? Number(p.area_m2) : null,
    atributos: normalizeAtributosFromDb(p.atributos),
  };
};

// Helper function to convert local product to Supabase format
const convertLocalToSupabase = (p: Partial<Producto>, empresaId: string) => {
  // Generate slug from nombre if not provided
  const _generateSlug = (nombre: string) => {
    return nombre
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const tipoOfertaFinal: Producto['tipoOferta'] =
    (p.tipoOferta as Producto['tipoOferta']) ||
    (p.esSuscripcion ? 'membresia' : p.inventory_type === 'unlimited' ? 'servicio' : 'producto');
  const inventoryTypeFinal: 'tracked' | 'unlimited' =
    tipoOfertaFinal === 'servicio' || tipoOfertaFinal === 'membresia' || tipoOfertaFinal === 'listado'
      ? 'unlimited'
      : (p.inventory_type === 'unlimited' ? 'unlimited' : 'tracked');

  return {
    ...(p.id ? { id: p.id } : {}),
    empresa_id: empresaId,
    nombre: p.nombre || '',
    descripcion: p.descripcion || '',
    codigo: p.sku || '',
    ...(p.barcode !== undefined && p.barcode !== '' && { barcode: p.barcode }),
    ...(p.productoGlobalId !== undefined && {
      producto_global_id: p.productoGlobalId || null,
    }),
    ...(p.sku !== undefined && p.sku !== '' && { sku: p.sku }),
    categoria: p.categoria || '',
    precio: p.precioVenta || 0,
    precio_compra: p.precioCompra || 0,
    stock: p.stock ?? 0,
    stock_minimo:
      p.stockMinimo !== undefined && p.stockMinimo !== null
        ? p.stockMinimo
        : (inventoryTypeFinal === 'unlimited' ? 0 : 5),
    unidad_medida: p.unidadMedida || 'Unidad',
    inventory_type: inventoryTypeFinal,
    mostrar_en_catalogo: String(p.estado || '').toUpperCase() === 'ARCHIVADO'
      ? false
      : p.mostrarEnCatalogo !== false,
    destacado: p.destacado || false,
    disponible_online: String(p.estado || '').toUpperCase() === 'ARCHIVADO'
      ? false
      : p.disponibleOnline !== false,
    activo: !['INACTIVO', 'ARCHIVADO'].includes(String(p.estado || '').toUpperCase()),
    ...(p.estado != null && String(p.estado).trim() !== ''
      ? {
          estado: String(p.estado).trim().toUpperCase(),
          ...(String(p.estado).toUpperCase() === 'ARCHIVADO'
            ? { fecha_archivado: new Date().toISOString() }
            : {}),
        }
      : {}),
    imagen: (() => {
      const list = filterValidImageUrls(Array.isArray(p.imagenes) ? p.imagenes : (p.imagen ? [p.imagen] : []));
      return list.length > 0 ? list[0] : (p.imagen && String(p.imagen).trim() ? p.imagen : '');
    })(),
    ...(filterValidImageUrls(Array.isArray(p.imagenes) ? p.imagenes : (p.imagen ? [p.imagen] : [])).length > 0 && {
      imagenes: filterValidImageUrls(Array.isArray(p.imagenes) ? p.imagenes : (p.imagen ? [p.imagen] : []))
    }),
    iva_porcentaje: p.ivaPorcentaje || 0, // CRITICAL: Save IVA percentage to database
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    // Subscription fields (if productos table has these columns)
    ...(p.esSuscripcion !== undefined && { es_suscripcion: p.esSuscripcion }),
    ...(p.frecuenciaCobro !== undefined && { frecuencia_cobro: p.frecuenciaCobro }),
    ...(p.clasesIncluidas !== undefined && { clases_incluidas: p.clasesIncluidas }),
    ...(p.duracionDias !== undefined && { duracion_dias: p.duracionDias }),
    ...(p.autoRenovar !== undefined && { auto_renovar: p.autoRenovar }),
    ...(p.generaQrCode !== undefined && { genera_qr_code: p.generaQrCode }),
    ...(p.requiereCheckin !== undefined && { requiere_checkin: p.requiereCheckin }),
    tipo_oferta: tipoOfertaFinal,
    ...((): Record<string, boolean> => {
      const ta =
        (p as { tieneAgenda?: boolean }).tieneAgenda !== undefined
          ? !!(p as { tieneAgenda?: boolean }).tieneAgenda
          : (p as { isBookable?: boolean }).isBookable !== undefined
            ? !!(p as { isBookable?: boolean }).isBookable
            : undefined;
      return ta !== undefined ? { tiene_agenda: ta } : {};
    })(),
    ...(p.accesoLibre !== undefined && { acceso_libre: p.accesoLibre }),
    ...(p.incluyeGrupal !== undefined && { incluye_grupal: p.incluyeGrupal }),
    ...(p.incluyePrivada !== undefined && { incluye_privada: p.incluyePrivada }),
    ...(p.tipoListado !== undefined && {
      tipo_listado:
        p.tipoListado === '' || p.tipoListado == null ? null : p.tipoListado,
    }),
    ...(p.ubicacionListado !== undefined && {
      ubicacion_listado: p.ubicacionListado || null,
    }),
    ...(p.areaM2 !== undefined && { area_m2: p.areaM2 }),
    ...(p.atributos !== undefined && {
      atributos: (() => {
        const rows = normalizeAtributosFromDb(p.atributos);
        const payload = atributosRowsToDbPayload(rows);
        return payload.length > 0 ? payload : null;
      })(),
    }),
  };
};

export const useProductosStore = create<ProductosState>((set, get) => ({
  productos: [],
  productoSeleccionado: null,
  isLoading: false,
  error: null,
  filtros: {
    categoria: '',
    estado: ''
  },
  paginacion: {
    pagina: 1,
    porPagina: 20,
    total: 0
  },

  fetchProductos: async (empresaId: string) => {
    const existing = fetchProductosInFlight.get(empresaId);
    if (existing) return existing;

    const run = (async (): Promise<Producto[] | null> => {
      try {
        return await handleStoreAction(
          async () => {
            logger.debug('🔍 Fetching productos for empresa:', empresaId);

            if (!empresaId) {
              logger.warn('❌ No empresa ID provided');
              set({ productos: [] });
              return [];
            }

            const { data, error } = await fetchByEmpresaId<any>(
              'productos',
              empresaId,
              { timeout: 15000 } // Keep the 15 second timeout
            );

            // Handle specific error types with custom messages
            if (error) {
              const errorMessage = error.message || '';

              // RLS error - return empty array instead of error
              if (errorMessage.includes('row-level security')) {
                logger.warn('⚠️ RLS error detected, returning empty products instead of error');
                set({ productos: [] });
                return [];
              }

              // Timeout error - custom Spanish message
              if (errorMessage.includes('timeout')) {
                logger.warn('⚠️ Request timeout - this might be due to large image payloads');
                throw new Error(
                  'La carga está tardando demasiado. Esto puede deberse a imágenes muy grandes. Intenta recargar la página.'
                );
              }

              // Network error - custom Spanish message
              if (errorMessage.includes('network') || errorMessage.includes('Failed to fetch')) {
                logger.warn('⚠️ Network error detected');
                throw new Error('Error de conexión. Verifica tu internet e intenta de nuevo.');
              }

              // Other errors - throw to be handled by handleStoreAction
              throw error;
            }

            logger.debug('📦 Productos cargados desde Supabase:', data?.length || 0, 'items');

            // Convert to local format (keep existing conversion logic)
            const productosLocales = (data || []).map(convertSupabaseToLocal);
            set({ productos: productosLocales });

            return productosLocales;
          },
          (error) => set({ error }),
          (loading) => set({ isLoading: loading })
        );
      } finally {
        fetchProductosInFlight.delete(empresaId);
      }
    })();

    fetchProductosInFlight.set(empresaId, run);
    return run;
  },

  fetchProducto: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const supabaseProducto = await productoService.getById(id);
      if (supabaseProducto) {
        const producto = convertSupabaseToLocal(supabaseProducto);
        set({ productoSeleccionado: producto, isLoading: false });
      } else {
        set({ error: 'Producto no encontrado', isLoading: false });
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error al cargar producto',
        isLoading: false
      });
    }
  },

  crearProducto: async (producto: Partial<Producto>, empresaId: string) => {
    set({ isLoading: true, error: null });
    try {
      logger.debug('🔄 Creating product:', producto.nombre);
      
      if (!empresaId) {
        logger.error('❌ No empresaId provided');
        set({
          productos: [],
          isLoading: false,
          error: 'Para crear productos, primero necesitas crear una empresa. Ve al dashboard y haz clic en "Nueva Empresa".'
        });
        return null;
      }
      
      // Convert to Supabase format using provided empresa ID
      const supabaseProducto = convertLocalToSupabase(producto, empresaId);
      
      // Allow empty single imagen if we have imagenes array
      if ((!supabaseProducto.imagen || supabaseProducto.imagen.trim() === '') && (!supabaseProducto.imagenes || supabaseProducto.imagenes.length === 0)) {
        logger.warn('⚠️ Creating product with no images');
      }
      
      // Create in Supabase
      const { data: createdProducto, error: createError } = await supabase
        .from('productos')
        .insert(supabaseProducto)
        .select()
        .single();

      if (createError) {
        logger.error('❌ Error creating producto:', {
          message: createError.message,
          details: createError.details,
          code: createError.code
        });
        throw createError;
      }
      
      logger.info('✅ Producto creado en Supabase:', createdProducto.id);
      
      // Convert back to local format
      const nuevoProducto = convertSupabaseToLocal(createdProducto);
      
      // Add to local state immediately for UI responsiveness
      const updatedProductos = [...get().productos, nuevoProducto];
      set(_state => ({
        productos: updatedProductos,
        isLoading: false
      }));
      
      // CRITICAL: Refresh productos list to ensure it's in sync with database
      logger.debug('🔄 Refreshing productos list after creation');
      try {
        await get().fetchProductos(empresaId);
      } catch (refreshError) {
        logger.warn('⚠️ Error refreshing productos after creation, but product was created:', refreshError);
      }
      
      return nuevoProducto;
      
    } catch (error) {
      logger.error('❌ Error creating producto:', error);
      set({
        error: error instanceof Error ? error.message : 'Error al crear producto en Supabase',
        isLoading: false
      });
      return null;
    }
  },

  actualizarProducto: async (id: string, producto: Partial<Producto>, empresaId: string) => {
    set({ isLoading: true, error: null });
    try {
      console.log('🔄 Updating product:', producto);
      console.log('🖼️ IMAGEN BEFORE UPDATE CONVERSION:', producto.imagen);
      
      if (!empresaId) {
        throw new Error('Usuario no tiene empresa asignada');
      }
      
      // Convert to Supabase format using provided empresa ID
      const supabaseProducto = convertLocalToSupabase(producto, empresaId);
      
      console.log('🖼️ IMAGEN AFTER UPDATE CONVERSION:', supabaseProducto.imagen);
      console.log('🔍 PRODUCT TO UPDATE:', JSON.stringify(supabaseProducto, null, 2));
      
      // Update in Supabase
      const updatedProducto = await productoService.update(id, supabaseProducto);
      console.log('✅ Producto actualizado en Supabase:', updatedProducto);
      
      // Convert back to local format
      const productoLocal = convertSupabaseToLocal(updatedProducto);
      
      // Update local state
      const updatedProductos = get().productos.map(p => 
        p.id === id ? productoLocal : p
      );
      set(_state => ({
        productos: updatedProductos,
        isLoading: false
      }));

      // Alert when tracked product is at or below minimum stock
      const invType = (productoLocal as Producto & { inventory_type?: string }).inventory_type;
      if (invType !== 'unlimited' && productoLocal.stock <= (productoLocal.stockMinimo ?? 0)) {
        // Disabled — inventory alerts are a nightly digest now (see migration 20260619120000).
      }
      
      return productoLocal;
      
    } catch (error) {
      console.error('Error updating producto:', error);
      set({
        error: error instanceof Error ? error.message : 'Error al actualizar producto',
        isLoading: false
      });
      return null;
    }
  },

  actualizarStockEnLinea: async (id: string, stock: number, stockMinimo: number, empresaId: string) => {
    const stockN = Math.max(0, Math.floor(Number(stock)));
    const minN = Math.max(0, Math.floor(Number(stockMinimo)));
    set({ error: null });
    try {
      const { data, error } = await supabase
        .from('productos')
        .update({
          stock: stockN,
          stock_minimo: minN,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      const productoLocal = convertSupabaseToLocal(data);
      set((state) => ({
        productos: state.productos.map((p) => (p.id === id ? productoLocal : p)),
      }));

      const invType = (productoLocal as Producto & { inventory_type?: string }).inventory_type;
      if (
        empresaId &&
        invType !== 'unlimited' &&
        productoLocal.stock <= (productoLocal.stockMinimo ?? 0)
      ) {
        // Disabled — inventory alerts are a nightly digest now (see migration 20260619120000).
      }

      return true;
    } catch (err) {
      logger.error('Error actualizando stock en línea:', err);
      set({
        error:
          err instanceof Error ? err.message : 'No se pudo actualizar el inventario',
      });
      return false;
    }
  },

  eliminarProducto: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      // Remove references from member_subscriptions so product delete does not hit FK constraint
      const { error: subsError } = await supabase
        .from('member_subscriptions')
        .delete()
        .eq('producto_id', id);
      if (subsError) {
        logger.warn('Could not clear member_subscriptions for product', { id, error: subsError });
        // Continue with delete; migration may have CASCADE, or there may be no rows
      }
      // Delete from Supabase
      await productoService.delete(id);
      console.log('✅ Producto eliminado de Supabase:', id);
      
      // Remove from local state
      const updatedProductos = get().productos.filter(p => p.id !== id);
      set(_state => ({
        productos: updatedProductos,
        isLoading: false
      }));
      
      return true;
      
    } catch (error) {
      console.error('❌ Error deleting producto:', error);
      set({
        error: error instanceof Error ? error.message : 'Error al eliminar producto',
        isLoading: false
      });
      return false;
    }
  },

  archivarProducto: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('productos')
        .update({
          estado: 'ARCHIVADO',
          activo: false,
          mostrar_en_catalogo: false,
          disponible_online: false,
          fecha_archivado: now,
          updated_at: now,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      const productoLocal = convertSupabaseToLocal(data);
      set({
        productos: get().productos.map((p) => (p.id === id ? productoLocal : p)),
        isLoading: false,
      });
      return true;
    } catch (error) {
      logger.error('Error archivando producto:', error);
      set({
        error: error instanceof Error ? error.message : 'Error al archivar producto',
        isLoading: false,
      });
      return false;
    }
  },

  restaurarProducto: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const existing = get().productos.find((p) => p.id === id);
      const sinStock =
        existing &&
        (existing as { inventory_type?: string }).inventory_type !== 'unlimited' &&
        (existing.stock ?? 0) === 0;
      const nuevoEstado = sinStock ? 'AGOTADO' : 'ACTIVO';
      const now = new Date().toISOString();

      const { data, error } = await supabase
        .from('productos')
        .update({
          estado: nuevoEstado,
          activo: true,
          fecha_archivado: null,
          updated_at: now,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      const productoLocal = convertSupabaseToLocal(data);
      set({
        productos: get().productos.map((p) => (p.id === id ? productoLocal : p)),
        isLoading: false,
      });
      return true;
    } catch (error) {
      logger.error('Error restaurando producto:', error);
      set({
        error: error instanceof Error ? error.message : 'Error al restaurar producto',
        isLoading: false,
      });
      return false;
    }
  },

  ajustarStock: async (id: string, cantidad: number, tipo: 'ENTRADA' | 'SALIDA') => {
    set({ isLoading: true, error: null });
    try {
      const producto = get().productos.find(p => p.id === id);
      if (!producto) {
        throw new Error('Producto no encontrado');
      }
      
      const nuevoStock = tipo === 'ENTRADA' 
        ? producto.stock + cantidad 
        : producto.stock - cantidad;
      
      // Update stock in Supabase
      await productoService.update(id, { stock: nuevoStock });
      console.log(`✅ Stock ajustado en Supabase: ${tipo} ${cantidad} unidades`);
      
      // Update local state
      const updatedProductos = get().productos.map(p => 
        p.id === id ? { ...p, stock: nuevoStock } : p
      );
      set(_state => ({
        productos: updatedProductos,
        isLoading: false
      }));

      // Alert when tracked product is at or below minimum stock after adjustment
      const invType = (producto as Producto & { inventory_type?: string }).inventory_type;
      if (invType !== 'unlimited' && nuevoStock <= (producto.stockMinimo ?? 0)) {
        // Disabled — inventory alerts are a nightly digest now (see migration 20260619120000).
      }
      
    } catch (error) {
      console.error('Error adjusting stock:', error);
      set({
        error: error instanceof Error ? error.message : 'Error al ajustar stock',
        isLoading: false
      });
    }
  },

  ajustarStockMultiple: async (items: Array<{ productoId: string; cantidad: number }>, tipo: 'ENTRADA' | 'SALIDA') => {
    set({ isLoading: true, error: null });
    try {
      logger.info(`🔄 Adjusting stock for ${items.length} product(s) (${tipo})...`);
      
      // CRITICAL: Ensure productos are loaded
      if (get().productos.length === 0) {
        logger.warn('⚠️ Productos array is empty, fetching from database...');
        const { useAuthStore } = await import('../stores/useAuthStore');
        const currentEmpresaId = useAuthStore.getState().currentEmpresa?.id;
        if (currentEmpresaId) {
          await get().fetchProductos(currentEmpresaId);
        }
      }
      
      const results: Array<{ productoId: string; success: boolean; error?: string; previousStock?: number; newStock?: number }> = [];
      const { useAuthStore } = await import('../stores/useAuthStore');
      const currentEmpresaId = useAuthStore.getState().currentEmpresa?.id;
      
      if (!currentEmpresaId) {
        throw new Error('No empresa selected');
      }
      
      // Process each item
      for (const item of items) {
        let producto = get().productos.find(p => p.id === item.productoId);
        
        if (!producto) {
          logger.error('❌ Product not found in store:', {
            productoId: item.productoId,
            empresaId: currentEmpresaId,
            loadedProductsCount: get().productos.length,
            loadedProductIds: get().productos.map(p => p.id).slice(0, 5)
          });
          
          // Try fetching this specific product from database
          const { data: dbProduct, error: fetchError } = await supabase
            .from('productos')
            .select('*')
            .eq('id', item.productoId)
            .eq('empresa_id', currentEmpresaId)
            .maybeSingle();

          if (dbProduct && !fetchError) {
            logger.info('✅ Product found in database, adding to store');
            const convertedProduct = convertSupabaseToLocal(dbProduct);
            set(_state => ({
              productos: [..._state.productos, convertedProduct]
            }));
            producto = convertedProduct;
          } else {
            logger.error('❌ Product not found in database either:', {
              productoId: item.productoId,
              empresaId: currentEmpresaId,
              fetchError: fetchError?.message
            });
            
            results.push({
              productoId: item.productoId,
              success: false,
              error: 'Product not found in database'
            });
            continue;
          }
        }
        
        const invType = (producto as Producto & { inventory_type?: string }).inventory_type;
        if (tipo === 'SALIDA' && invType === 'unlimited') {
          results.push({
            productoId: item.productoId,
            success: true,
            previousStock: producto.stock,
            newStock: producto.stock,
          });
          continue;
        }

        const nuevoStock = tipo === 'ENTRADA' 
          ? (producto.stock || 0) + item.cantidad 
          : (producto.stock || 0) - item.cantidad;
        
        // Check for negative stock
        if (nuevoStock < 0) {
          logger.error('❌ Insufficient stock:', {
            producto: producto.nombre,
            currentStock: producto.stock,
            requested: item.cantidad
          });
          
          results.push({
            productoId: item.productoId,
            success: false,
            error: 'Insufficient stock',
            previousStock: producto.stock
          });
          continue;
        }
        
        // Update stock in Supabase
        const { error: updateError } = await supabase
          .from('productos')
          .update({ stock: nuevoStock })
          .eq('id', item.productoId);
        
        if (updateError) {
          logger.error('❌ Database error updating stock:', updateError);
          results.push({
            productoId: item.productoId,
            success: false,
            error: updateError.message,
            previousStock: producto.stock
          });
          continue;
        }
        
        // Update local state
        set(_state => ({
          productos: _state.productos.map(p =>
            p.id === item.productoId
              ? { ...p, stock: nuevoStock }
              : p
          )
        }));
        
        logger.debug(`✅ Stock adjusted: ${producto.nombre} - ${tipo} ${item.cantidad} units (new stock: ${nuevoStock})`);

        if (invType !== 'unlimited' && nuevoStock <= (producto.stockMinimo ?? 0)) {
          // Disabled — inventory alerts are a nightly digest now (see migration 20260619120000).
        }
        
        results.push({
          productoId: item.productoId,
          success: true,
          previousStock: producto.stock,
          newStock: nuevoStock
        });
      }
      
      const successCount = results.filter(r => r.success).length;
      logger.info(`✅ Stock adjusted for ${successCount}/${results.length} products`);
      set({ isLoading: false });
      
      return results;
    } catch (error) {
      logger.error('❌ Error adjusting stock for multiple items:', error);
      set({
        error: error instanceof Error ? error.message : 'Error al ajustar stock múltiple',
        isLoading: false
      });
      throw error; // Re-throw so caller can handle it
    }
  },

  setProductoSeleccionado: (producto: Producto | null) => {
    set({ productoSeleccionado: producto });
  },

  setFiltros: (filtros: any) => {
    set({ filtros: { ...get().filtros, ...filtros } });
  },

  setPaginacion: (paginacion: any) => {
    set({ paginacion: { ...get().paginacion, ...paginacion } });
  },

  clearError: () => {
    set({ error: null });
  },

  // Clear all state (useful for logout or page changes)
  clearState: () => {
    set({
      productos: [],
      productoSeleccionado: null,
      isLoading: false,
      error: null
    });
  },

  uploadProductImages: async (productoId: string, files: File[]): Promise<string[]> => {
    const { useAuthStore } = await import('../stores/useAuthStore');
    const empresaId = useAuthStore.getState().currentEmpresa?.id;
    if (!empresaId) throw new Error('No empresa selected');
    const uploadedUrls: string[] = [];
    for (const file of files) {
      try {
        const fileExt = file.name.split('.').pop() || 'jpg';
        const fileName = `${empresaId}/${productoId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const { error } = await supabase.storage
          .from('productos')
          .upload(fileName, file, { cacheControl: '3600', upsert: false });
        if (error) {
          console.error('Upload error:', error);
          continue;
        }
        const { data: { publicUrl } } = supabase.storage.from('productos').getPublicUrl(fileName);
        uploadedUrls.push(publicUrl);
      } catch (err) {
        console.error('Error uploading image:', err);
      }
    }
    if (uploadedUrls.length === 0) throw new Error('No images uploaded successfully');
    const producto = get().productos.find(p => p.id === productoId);
    const currentImages = filterValidImageUrls(producto?.imagenes || (producto?.imagen ? [producto.imagen] : []));
    const updatedImages = filterValidImageUrls([...currentImages, ...uploadedUrls]);
    const { error: updateError } = await supabase
      .from('productos')
      .update({ imagenes: updatedImages, imagen: updatedImages[0] || '' })
      .eq('id', productoId);
    if (updateError) throw updateError;
    set(_state => ({
      productos: _state.productos.map(p =>
        p.id === productoId ? { ...p, imagenes: updatedImages, imagen: updatedImages[0] || p.imagen } : p
      )
    }));
    return uploadedUrls;
  },

  removeProductImage: async (productoId: string, imageIndex: number): Promise<void> => {
    const producto = get().productos.find(p => p.id === productoId);
    if (!producto) return;
    const arr = filterValidImageUrls(producto.imagenes || (producto.imagen ? [producto.imagen] : []));
    const imageUrl = arr[imageIndex];
    const updatedImages = arr.filter((_, i) => i !== imageIndex);
    const { error } = await supabase
      .from('productos')
      .update({ imagenes: updatedImages, imagen: updatedImages[0] || '' })
      .eq('id', productoId);
    if (error) throw error;
    set(_state => ({
      productos: _state.productos.map(p =>
        p.id === productoId ? { ...p, imagenes: updatedImages, imagen: updatedImages[0] || '' } : p
      )
    }));
    try {
      if (imageUrl) {
        const url = new URL(imageUrl);
        const pathMatch = url.pathname.match(/\/storage\/v1\/object\/public\/productos\/(.+)$/);
        if (pathMatch && pathMatch[1]) await supabase.storage.from('productos').remove([pathMatch[1]]);
      }
    } catch (_) { /* ignore */ }
  },

  reorderProductImages: async (productoId: string, fromIndex: number, toIndex: number): Promise<void> => {
    const producto = get().productos.find(p => p.id === productoId);
    if (!producto) return;
    const arr = filterValidImageUrls(producto.imagenes || (producto.imagen ? [producto.imagen] : []));
    const [removed] = arr.splice(fromIndex, 1);
    if (removed === undefined) return;
    arr.splice(toIndex, 0, removed);
    const updatedImages = [...arr];
    const { error } = await supabase
      .from('productos')
      .update({ imagenes: updatedImages, imagen: updatedImages[0] || '' })
      .eq('id', productoId);
    if (error) throw error;
    set(_state => ({
      productos: _state.productos.map(p =>
        p.id === productoId ? { ...p, imagenes: updatedImages, imagen: updatedImages[0] || '' } : p
      )
    }));
  },

  setPrimaryImage: async (productoId: string, imageIndex: number): Promise<void> => {
    await get().reorderProductImages(productoId, imageIndex, 0);
  },

  findByBarcode: (barcode: string): Producto | undefined => {
    const trimmed = (barcode || '').trim();
    if (!trimmed) return undefined;
    return get().productos.find(
      p => p.barcode === trimmed || p.sku === trimmed || (p as any).codigo === trimmed
    );
  },

  generateSKU: async (empresaId: string, categoria?: string): Promise<string> => {
    try {
      const { data, error } = await supabase.rpc('generate_sku', {
        p_empresa_id: empresaId,
        p_categoria: categoria || 'PRO'
      });
      if (error) throw error;
      if (typeof data === 'string') return data;
      return String(data);
    } catch (err) {
      logger.debug('generate_sku RPC failed, using client fallback', err);
      const prefix = (categoria || 'PRO').substring(0, 3).toUpperCase().replace(/[^A-Z]/g, 'P');
      const count = get().productos.length + 1;
      return `${prefix}-${String(count).padStart(4, '0')}`;
    }
  }
}));
